import { AHInput, AHText, Box, useTheme } from "arkh";
import React, { useCallback, useLayoutEffect, useState } from "react";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { OrgSpaceEdit_space$key } from "./__generated__/OrgSpaceEdit_space.graphql";
import { OrgSpaceEditQuery } from "./__generated__/OrgSpaceEditQuery.graphql";
import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { HeaderButton } from "../../../components";
import { useAlert } from "../../../common/Alert";
import { FormattedMessage, useIntl } from "react-intl";
import { OrgSpaceCreatePageNameSubTitle } from "../OrgSpaceCreate/OrgSpaceCreatePageName";
import { OrgButton } from "../OrgButton";
import { useNavigationHeaderOptions } from "../../../common/navigationHeaderOptions";

export const OrgSpaceEdit = withWebModal(OrgSpaceEditNavigator);

type ParamList = {
  OrgSpaceEditMain: {
    spaceId: string;
  };
};

const Stack = createNativeStackNavigator<ParamList>();

type Props = {
  route: RouteProp<ParamList, "OrgSpaceEditMain">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"OrgSpaceEdit">,
    NativeStackNavigationProp<ParamList, "OrgSpaceEditMain">
  >;
};

function OrgSpaceEditNavigator(
  props: UserLoggedInNavigatorScreenProps<"OrgSpaceEdit">
) {
  const intl = useIntl();
  const getOptions = useNavigationHeaderOptions();
  const theme = useTheme();
  return (
    <Stack.Navigator
      screenOptions={{
        contentStyle: { backgroundColor: theme["surface-background-light"] },
      }}
    >
      <Stack.Screen
        name="OrgSpaceEditMain"
        component={OrgSpaceEditScreen}
        options={{
          ...getOptions({
            type: "SAVE",
            onCancel: () => props.navigation.goBack(),
            onSave: () => {},
            saveLoading: false,
            title: intl.formatMessage({
              defaultMessage: "Space settings",
              description: "OrgSpaceEdit header title",
            }),
          }),
        }}
      />
    </Stack.Navigator>
  );
}

function OrgSpaceEditScreen(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerOrgSpaceEditScreen {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerOrgSpaceEditScreen(props: { fetchKey: string } & Props) {
  const data = useLazyLoadQuery<OrgSpaceEditQuery>(
    graphql`
      query OrgSpaceEditQuery($id: ID!) {
        storecastSpace(id: $id) {
          ...OrgSpaceEdit_space
        }
      }
    `,
    {
      id: props.route.params.spaceId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  return (
    <OrgSpaceEditComponent
      space={data.storecastSpace}
      navigation={props.navigation}
    />
  );
}

function OrgSpaceEditComponent(props: {
  space: OrgSpaceEdit_space$key;
  navigation: Props["navigation"];
}) {
  const intl_tokens = useIntlTokens();
  const intl = useIntl();
  const alert = useAlert();
  const space = useFragment(
    graphql`
      fragment OrgSpaceEdit_space on StorecastSpace {
        id
        name
      }
    `,
    props.space
  );
  const [name, setName] = useState(space.name ?? "");

  const [update] = useMutation(graphql`
    mutation OrgSpaceEditUpdateMutation($id: ID!, $name: String!) {
      storecastSpace(id: $id) {
        updateSpace(input: { setName: $name }) {
          id
          name
        }
      }
    }
  `);

  const [deleteSpace] = useMutation(graphql`
    mutation OrgSpaceEditDeleteMutation($id: ID!) {
      storecastSpace(id: $id) {
        delete {
          id
        }
      }
    }
  `);

  const onSave = useCallback(() => {
    update({
      variables: {
        id: space.id,
        name,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [name, props.navigation, space.id, update]);

  useLayoutEffect(() => {
    props.navigation.setOptions({
      headerRight: () => <HeaderButton type="SAVE" onPress={onSave} />,
    });
  }, [onSave, props.navigation, props.navigation.setOptions]);

  const onDelete = useCallback(() => {
    deleteSpace({
      variables: {
        id: space.id,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [deleteSpace, props.navigation, space.id]);
  const DELETE_SPACE_TITLE = intl.formatMessage({
    defaultMessage: "Delete this space",
    description: "OrgSpaceEdit delete section header",
  });
  const onDeleteAlert = useCallback(() => {
    alert.alert(
      DELETE_SPACE_TITLE,
      intl.formatMessage({
        defaultMessage: "Are you sure you want to delete this space?",
        description: "Org space edit delete prompt",
      }),
      [
        {
          text: intl_tokens.Delete,
          onPress: onDelete,
          style: "destructive",
        },
        {
          text: intl_tokens.Cancel,
          onPress: () => {},
        },
      ]
    );
  }, [
    DELETE_SPACE_TITLE,
    alert,
    intl,
    intl_tokens.Cancel,
    intl_tokens.Delete,
    onDelete,
  ]);

  return (
    <Box
      space="medium"
      color="background"
      style={{
        flex: 1,
      }}
    >
      <Box spaceHorizontal="medium" spaceBottom="medium">
        <AHText type="title-small">{intl_tokens.Name}</AHText>
        <AHText spaceTop="small">{OrgSpaceCreatePageNameSubTitle}</AHText>
      </Box>
      <AHInput
        value={name}
        onChangeText={setName}
        color="surface"
        space="medium"
        cornerRadius="large"
        textType="title-small"
        placeholder={intl_tokens.Name}
      />
      <Box spaceBottom="xxlarge" />
      <Box
        borderColor="negative"
        style={{ borderWidth: 1 }}
        cornerRadius="large"
        space="medium"
      >
        <AHText type="title-small" color="negative">
          {DELETE_SPACE_TITLE}
        </AHText>
        <AHText spaceTop="small" spaceBottom="medium">
          <FormattedMessage
            defaultMessage="You cannot restore a Space after it has been deleted. The space will be deleted from all dashboards."
            description="OrgSpaceEdit delete section details"
          />
        </AHText>
        <OrgButton
          title={intl_tokens.Delete}
          backgroundColor="negative"
          tintColor="surface"
          onPress={onDeleteAlert}
        />
      </Box>
    </Box>
  );
}
